<script lang="ts">
	import type { ChangeEventHandler } from 'svelte/elements';

	import { page } from '$app/state';
	import { mediaUrl } from '$lib';
	import Picto24h from '$lib/components/product/Picto24h.svelte';
	import { m } from '$lib/paraglide/messages.js';

	interface Variant {
		variation?: string;
		is_sold_out: boolean;
		lead_time: number;
		in_stock: boolean;
	}

	let {
		variants,
		selectedVariation = $bindable(),
		sizeGuide,
		onchange,
	}: {
		variants: Variant[];
		selectedVariation: string | null | undefined;
		sizeGuide?: string;
		onchange?: ChangeEventHandler<HTMLSelectElement | HTMLInputElement>;
	} = $props();

	const variationsShouldUseSelect = $derived(
		variants.some((variant) => variant.variation && variant.variation.length > 8) ||
			variants.length > 18,
	);
</script>

<div class="group/variation flex flex-col">
	{#if variationsShouldUseSelect}
		<label>
			{@render label()}
			<select
				bind:value={selectedVariation}
				class={[
					'w-full rounded-sm border border-black bg-white px-3 py-2 group-has-[:user-invalid]/variation:border-red-600',
				]}
				required
				{onchange}
				oninvalid={(e) => e.preventDefault()}
			>
				<option hidden disabled selected value={null}>{m.choose_an_option()}...</option>
				{#each variants as variant (variant)}
					<option value={variant.variation} disabled={variant.is_sold_out}
						>{variant.variation}
						{#if !variant.is_sold_out && (variant.lead_time === 1 || variant.in_stock)}-- {m
								.shipped_in({
									days: variant.lead_time,
								})
								.toLowerCase()}{/if}</option
					>
				{/each}
			</select>
		</label>
	{:else}
		{@render label()}

		<div class="mt-2 grid grid-cols-[repeat(auto-fill,minmax(min(6rem,100%),1fr))] gap-2">
			{#each variants as variant (variant)}
				<label
					class={[
						'relative min-w-16 cursor-pointer rounded-sm border px-4 py-3 text-sm transition-colors',
						{
							'border-black bg-black text-white hover:bg-neutral-800':
								selectedVariation === variant.variation,
							'border-neutral-300 bg-white text-black hover:border-neutral-400 hover:bg-neutral-100':
								selectedVariation !== variant.variation && !variant.is_sold_out,
							'cursor-not-allowed border-neutral-200 bg-neutral-100 text-neutral-400':
								variant.is_sold_out,
							'group-has-[:user-invalid]/variation:border-red-600': !variant.is_sold_out,
						},
					]}
				>
					<input
						type="radio"
						name="variation"
						value={variant.variation}
						bind:group={selectedVariation}
						class="hidden"
						required
						{onchange}
						disabled={variant.is_sold_out}
						oninvalid={(e) => e.preventDefault()}
					/>
					<div class="text-center select-none">
						{variant.variation}
					</div>
					{#if variant.in_stock && !variant.is_sold_out}
						<Picto24h
							class="-top-0.25! -right-0.25! rounded-tr text-[0.65rem] in-[label:hover]:bg-black"
						/>
					{/if}
				</label>
			{/each}
		</div>
	{/if}

	<div class="mt-1 hidden text-xs text-red-600 group-has-[:user-invalid]/variation:block">
		{m.this_field_is_required()}
	</div>
</div>

{#snippet label()}
	<div class="flex items-end justify-between">
		<div class="flex items-start gap-1 text-xs">
			<div>{m.size()}</div>
			<div class="text-red-600">*</div>
		</div>
		{#if sizeGuide}
			<a
				class="text-sm underline"
				href={mediaUrl(page.data.store.media_base_url, `wysiwyg/brands/size_grids/${sizeGuide}`)}
				target="_blank">{m.size_guide()}</a
			>
		{/if}
	</div>
{/snippet}
